import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Building2, CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/apiClient";

export default function BusinessInquiry() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ company: "", contact_name: "", email: "", phone: "", locations: 1, workforce_size: 0, needs: "", timeline: "" });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/b2b/sales/qualify", { ...form, locations: Number(form.locations) || 1, workforce_size: Number(form.workforce_size) || 0 });
      setResult(data);
    } catch { /* handled below */ } finally { setBusy(false); }
  };

  if (result) return (
    <div className="min-h-[70vh] grid place-items-center bg-slate-50 px-4" data-testid="inquiry-success">
      <div className="bg-white rounded-2xl shadow-lg p-10 text-center max-w-lg">
        <CheckCircle2 className="w-14 h-14 text-teal-600 mx-auto" />
        <h1 className="font-display text-2xl font-bold text-blue-950 mt-4">Thanks — we've got it.</h1>
        <p className="text-slate-600 mt-2">Based on your workforce, our AI recommends <b>{result.recommended_product}</b>. A T5 specialist will reach out to schedule your demo.</p>
        <Button onClick={() => navigate("/pricing")} className="mt-6 bg-blue-900">View plans</Button>
      </div>
    </div>
  );

  return (
    <div className="bg-slate-50 min-h-screen" data-testid="business-inquiry-page">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-16">
        <div className="flex items-center gap-2 mb-1"><Building2 className="w-5 h-5 text-teal-600" /><p className="text-xs uppercase tracking-[0.16em] text-teal-600 font-semibold">T5 Sales Agent</p></div>
        <h1 className="font-display text-3xl lg:text-4xl font-bold text-blue-950">Tell us about your organization</h1>
        <p className="text-slate-600 mt-2">Answer a few questions and our AI will recommend the right T5 solution and schedule a demo.</p>
        <motion.form initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} onSubmit={submit} className="mt-8 bg-white rounded-2xl border border-slate-200 shadow-sm p-6 grid sm:grid-cols-2 gap-4">
          <Input required placeholder="Company name" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="inq-company" />
          <Input required placeholder="Your name" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} data-testid="inq-name" />
          <Input required type="email" placeholder="Work email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="inq-email" />
          <Input placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="inq-phone" />
          <Input type="number" placeholder="Number of locations" value={form.locations} onChange={(e) => setForm({ ...form, locations: e.target.value })} data-testid="inq-locations" />
          <Input type="number" placeholder="Approx. workforce size" value={form.workforce_size} onChange={(e) => setForm({ ...form, workforce_size: e.target.value })} data-testid="inq-workforce" />
          <Textarea className="sm:col-span-2" placeholder="What challenges are you solving? (recruiting, scheduling, training, compliance…)" value={form.needs} onChange={(e) => setForm({ ...form, needs: e.target.value })} data-testid="inq-needs" />
          <Input className="sm:col-span-2" placeholder="Timeline (e.g. this quarter)" value={form.timeline} onChange={(e) => setForm({ ...form, timeline: e.target.value })} data-testid="inq-timeline" />
          <Button type="submit" disabled={busy} data-testid="inq-submit" className="sm:col-span-2 bg-teal-600 hover:bg-teal-700 gap-2">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Get my recommendation & demo</Button>
        </motion.form>
      </div>
    </div>
  );
}
